import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type {
  ApplicationTestingTicketCreated,
  ApplicationTestingTicketRow,
} from '@/core/models/applicationTesting.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { applicationTestingService } from '@/core/services/applicationTesting';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Menu, useMenu } from '@/kit/Menu';
import { Paginator } from '@/kit/Paginator';
import { Select } from '@/kit/Select';
import {
  markTagsSaved,
  seedServerTags,
  setChosenTags,
  tagsHeldFor,
  tagsNeedSaving,
  subscribeToTags,
  TAG_CATALOGUE,
  tagsVersion,
  ticketTags,
  tagTone,
} from './applicationTestingTags';
import { ApplicationTestingSidebar } from './ApplicationTestingSidebar';
import { RaiseApplicationTesting } from './RaiseApplicationTesting';
import './SupportTicket.scss';
import './ApplicationTestingTickets.scss';

// Statuses and priorities live in applicationTestingStatus; re-exported for the pages that import them from here.
export {
  PriorityPill,
  priorityColor,
  priorityGroupOf,
  priorityOptions,
  renderPriorityLabel,
  renderStatusLabel,
  StatusPill,
  statusChoicesFor,
  statusColor,
  statusOptions,
} from './applicationTestingStatus';
import {
  PriorityPill,
  priorityOptions,
  renderPriorityLabel,
  renderStatusLabel,
  StatusPill,
  STATUS_PANEL_MIN_WIDTH,
  statusOptions,
} from './applicationTestingStatus';

/** A ticket's tags as coloured chips. */
export function TagChips({ tags }: { tags: string[] }) {
  return (
    <div className="application-testing-tags">
      {tags.map((tag) => {
        const tone = tagTone(tag);
        return (
          <span
            key={tag}
            className="application-testing-tag"
            style={{ background: tone.background, color: tone.color }}
          >
            {tag}
          </span>
        );
      })}
    </div>
  );
}

/**
 * A ticket's tags you can change, like its status and priority: click to tick or untick tags from the catalogue,
 * grouped by category. Clicks stay inside, so the row behind it does not open the ticket.
 */
export function TagPicker({
  ticketId,
  tags,
  onSaved,
}: {
  ticketId: string;
  tags: string[];
  /** Called once the server has saved a change of tags (the ticket page refreshes its history from it). */
  onSaved?: () => void;
}) {
  const menu = useMenu();
  const latest = useRef({ tags, onSaved });
  useEffect(() => {
    latest.current = { tags, onSaved };
  });

  /** Saves the tags a moment after the last tick (and on leaving), so a burst of ticks is one change in the history. */
  const save = useCallback(() => {
    const { tags: current, onSaved: done } = latest.current;
    if (!tagsNeedSaving(ticketId, current)) return;
    const before = tagsHeldFor(ticketId);
    markTagsSaved(ticketId, current);
    applicationTestingService.setTags(ticketId, current).then(
      () => done?.(),
      (error) => {
        markTagsSaved(ticketId, before);
        notifyError(error);
      },
    );
  }, [ticketId]);

  const key = tags.join('|');
  useEffect(() => {
    if (!tagsNeedSaving(ticketId, latest.current.tags)) return;
    const timer = setTimeout(save, 700);
    return () => clearTimeout(timer);
  }, [key, ticketId, save]);
  useEffect(() => save, [save]);

  const toggle = (tag: string, checked: boolean) =>
    setChosenTags(ticketId, checked ? [...tags, tag] : tags.filter((t) => t !== tag));

  return (
    <div className="application-testing-tag-picker" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="application-testing-tag-trigger"
        title="Change tags"
        aria-label="Change tags"
        onClick={menu.onOpen}
      >
        {tags.length > 0 ? <TagChips tags={tags} /> : <span className="application-testing-tag-empty">Add tags</span>}
        <Icon name="edit" outlined className="application-testing-tag-edit" />
      </button>
      <Menu {...menu} className="application-testing-tag-menu" xPosition="after">
        {/* Ticking tags keeps the list open; it closes on a click outside it. */}
        <div className="application-testing-tag-options" onClick={(event) => event.stopPropagation()}>
          {TAG_CATALOGUE.map((group) => (
            <div key={group.category} className="application-testing-tag-group">
              <p className="application-testing-tag-group-title">{group.category}</p>
              {group.tags.map((tag) => (
                <Checkbox
                  key={tag}
                  checked={tags.includes(tag)}
                  onChange={(event) => toggle(tag, event.target.checked)}
                >
                  {tag}
                </Checkbox>
              ))}
            </div>
          ))}
          <div className="application-testing-tag-actions">
            <button type="button" className="application-testing-clear" onClick={() => setChosenTags(ticketId, [])}>
              Clear all
            </button>
            <button type="button" className="application-testing-tag-done" onClick={menu.onClose}>
              Done
            </button>
          </div>
        </div>
      </Menu>
    </div>
  );
}

type SortKey = 'ticket' | 'subject' | 'tags' | 'date' | 'status' | 'priority';
type SortState = { key: SortKey; direction: 'asc' | 'desc' } | null;

const MONTH_INDEX: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/** `date` is formatted as "12-Mar-2026" for display; parsed back to a timestamp so sorting is chronological, not lexical. */
export function dateSortValue(value: string): number {
  const match = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(value);
  if (!match) return 0;
  const [, day, month, year] = match;
  return new Date(Number(year), MONTH_INDEX[month] ?? 0, Number(day)).getTime();
}

function sortValueOf(row: ApplicationTestingTicketRow, key: SortKey): string | number {
  switch (key) {
    case 'ticket':
      return row.ticketNo;
    case 'subject':
      return row.subject;
    case 'tags':
      return ticketTags(row).join(', ');
    case 'date':
      return dateSortValue(row.date);
    case 'status':
      return row.statusId;
    case 'priority':
      return row.priorityId;
  }
}

const TAG_OPTIONS = TAG_CATALOGUE.flatMap((c) => c.tags.map((tag) => ({ id: tag, name: tag, group: c.category })));
/** Rows per page: 25 by default, up to 200. */
const PAGE_SIZES = [25, 50, 100, 200];

/** The row's single action, as a direct icon button. */
function RowMenu({ onOpen }: { onOpen: () => void }) {
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Button title="Open ticket" onClick={onOpen}>
        <Icon name="open_in_new" outlined />
      </Button>
    </div>
  );
}

/** The list as it was left: its filters, sort, page and the tickets it showed. */
interface ListView {
  searchText: string;
  status: number | null;
  priority: number | null;
  tag: string | null;
  sort: SortState;
  page: number;
  pageSize: number;
  rows: ApplicationTestingTicketRow[];
}

/**
 * The list as it was last left, kept while the app is open: opening a ticket and coming back ("Back to Application
 * Testing", or the browser's back) shows the list with the same filters, sort and page instead of reloading it blank and
 * unfiltered. Its tickets are then refreshed quietly with those filters.
 */
let lastView: ListView | null = null;

/** Forgets the list as it was left (tests start from a fresh list). */
export function forgetApplicationTestingListView() {
  lastView = null;
}

/** Every ticket raised asking for the app itself to be improved: search, tag, status and priority filters. */
export function ApplicationTestingTickets() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [restored] = useState(() => lastView);
  const [dataSource, setDataSource] = useState<ApplicationTestingTicketRow[]>(restored?.rows ?? []);
  const [searchText, setSearchText] = useState(restored?.searchText ?? '');
  const [statusFilter, setStatusFilter] = useState<number | null>(restored?.status ?? null);
  const [priorityFilter, setPriorityFilter] = useState<number | null>(restored?.priority ?? null);
  const [tagFilter, setTagFilter] = useState<string | null>(restored?.tag ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [sort, setSort] = useState<SortState>(restored?.sort ?? null);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [page, setPage] = useState(restored?.page ?? 1);
  const [pageSize, setPageSize] = useState(restored?.pageSize ?? PAGE_SIZES[0]);

  const filters = useRef<{ searchText: string; status: number | null; priority: number | null }>({
    searchText: restored?.searchText.trim() ?? '',
    status: restored?.status ?? null,
    priority: restored?.priority ?? null,
  });
  const saving = useRef(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /** Loads the tickets the filters ask for; back on page 1 unless `keepPage` (the quiet refresh of a list come back to). */
  const getList = useCallback((keepPage = false) => {
    const { searchText: text, status, priority } = filters.current;
    applicationTestingService.list({ searchText: text || null, status, priority }).then(
      (response) => {
        seedServerTags(response.ticketList);
        setDataSource(response.ticketList);
        if (!keepPage) setPage(1);
      },
      (error) => notifyError(error),
    );
  }, []);

  useEffect(() => {
    getList(!!restored);
    return () => clearTimeout(searchTimer.current);
  }, [getList, restored]);

  // The header's "Application Testing > New ticket" opens this page with ?new=1: raise the ticket dialog once, then drop the parameter so a refresh does not reopen it.
  const raiseRequested = searchParams.get('new') === '1';
  useEffect(() => {
    if (!raiseRequested) return;
    setSearchParams({}, { replace: true });
    openRaiseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raiseRequested]);

  useEffect(() => {
    lastView = {
      searchText,
      status: statusFilter,
      priority: priorityFilter,
      tag: tagFilter,
      sort,
      page,
      pageSize,
      rows: dataSource,
    };
  }, [searchText, statusFilter, priorityFilter, tagFilter, sort, page, pageSize, dataSource]);

  const onSearchChange = (text: string) => {
    setSearchText(text);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (filters.current.searchText === text.trim()) return;
      filters.current.searchText = text.trim();
      getList();
    }, 300);
  };

  const onStatusChange = (value: number | null) => {
    filters.current.status = value;
    setStatusFilter(value);
    getList();
  };

  const onPriorityChange = (value: number | null) => {
    filters.current.priority = value;
    setPriorityFilter(value);
    getList();
  };

  const onTagChange = (value: string | null) => {
    setTagFilter(value);
    setPage(1);
  };

  const hasFilters = !!searchText || statusFilter !== null || priorityFilter !== null || tagFilter !== null;

  const clearFilters = () => {
    clearTimeout(searchTimer.current);
    setSearchText('');
    setStatusFilter(null);
    setPriorityFilter(null);
    setTagFilter(null);
    filters.current = { searchText: '', status: null, priority: null };
    getList();
  };

  const setSaving = (value: boolean) => {
    saving.current = value;
    setIsSaving(value);
  };

  /** A row that no longer matches the active status/priority filters drops out of the grid. */
  const replace = (row: ApplicationTestingTicketRow) => {
    const { status, priority } = filters.current;
    const matchesStatus = status === null || status === row.statusId;
    const matchesPriority = priority === null || priority === row.priorityId;
    setDataSource((rows) =>
      matchesStatus && matchesPriority
        ? rows.map((r) => (r.id === row.id ? row : r))
        : rows.filter((r) => r.id !== row.id),
    );
  };

  const setStatus = (row: ApplicationTestingTicketRow, status: number) => {
    if (row.statusId === status || saving.current) {
      return;
    }
    setSaving(true);
    applicationTestingService
      .setStatus(row.id, status)
      .then(
        (saved) => {
          replace(saved);
          notifySuccess(`${saved.ticketNo} is now ${saved.status}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setSaving(false));
  };

  const setPriority = (row: ApplicationTestingTicketRow, priority: number) => {
    if (row.priorityId === priority || saving.current) {
      return;
    }
    setSaving(true);
    applicationTestingService
      .setPriority(row.id, priority)
      .then(
        (saved) => {
          replace(saved);
          notifySuccess(`${saved.ticketNo} priority set to ${saved.priority}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setSaving(false));
  };

  const openRaiseDialog = () => {
    void openDialog<unknown, ApplicationTestingTicketCreated>(RaiseApplicationTesting)
      .afterClosed()
      .then((result) => {
        if (result?.id) {
          notifySuccess(result.message);
          getList();
        }
      });
  };

  const openDetails = (row: ApplicationTestingTicketRow) => {
    navigate(`/home/application-testing/${row.id}`);
  };

  const toggleSort = (key: SortKey) => {
    setSort((current) => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const tagsSeen = useSyncExternalStore(subscribeToTags, tagsVersion);

  const filteredRows = useMemo(
    () => (tagFilter ? dataSource.filter((row) => ticketTags(row).includes(tagFilter)) : dataSource),
    // tagsVersion: a tag chosen by hand changes which rows the tag filter keeps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSource, tagFilter, tagsSeen],
  );

  const sortedRows = useMemo(() => {
    if (!sort) return filteredRows;
    const { key, direction } = sort;
    const factor = direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = sortValueOf(a, key);
      const bv = sortValueOf(b, key);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [filteredRows, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((row) => selection.has(row.id));
  const someOnPageSelected = pageRows.some((row) => selection.has(row.id));

  const toggleRow = (id: string, checked: boolean) =>
    setSelection((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const togglePage = (checked: boolean) =>
    setSelection((current) => {
      const next = new Set(current);
      pageRows.forEach((row) => (checked ? next.add(row.id) : next.delete(row.id)));
      return next;
    });

  /**
   * The ticked tickets the filters still show, in the grid's order. A ticket ticked and then filtered out is left
   * alone, so a bulk action never touches rows the user cannot see.
   */
  const selectedRows = sortedRows.filter((row) => selection.has(row.id));

  const exportSelected = () => {
    if (selectedRows.length === 0) return;
    const tags = Object.fromEntries(selectedRows.map((row) => [row.id, ticketTags(row)]));
    applicationTestingService
      .exportTickets(
        selectedRows.map((row) => row.id),
        tags,
      )
      .catch((error) => notifyError(error));
  };

  /** A column header that sorts the grid on click, with an arrow showing the active column and direction. */
  const sortableHeader = (label: string, key: SortKey) => (
    <button type="button" className="application-testing-sort-header" onClick={() => toggleSort(key)}>
      <span>{label}</span>
      <Icon
        name={sort?.key === key ? (sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
        className={sort?.key === key ? 'text-primary' : 'text-gray-200'}
      />
    </button>
  );

  const columns: DataTableColumn<ApplicationTestingTicketRow>[] = [
    {
      id: 'select',
      header: (
        <Checkbox
          aria-label="Select all on this page"
          checked={allOnPageSelected}
          indeterminate={someOnPageSelected && !allOnPageSelected}
          onChange={(event) => togglePage(event.target.checked)}
        />
      ),
      headerClassName: 'application-testing-select-cell',
      cellClassName: 'application-testing-select-cell',
      cell: (row) => (
        <div onClick={(event) => event.stopPropagation()}>
          <Checkbox
            aria-label={`Select ${row.ticketNo}`}
            checked={selection.has(row.id)}
            onChange={(event) => toggleRow(row.id, event.target.checked)}
          />
        </div>
      ),
    },
    {
      id: 'ticket',
      header: sortableHeader('Ticket No.', 'ticket'),
      headerClassName: 'w-130',
      cell: (row) => row.ticketNo,
    },
    {
      id: 'subject',
      header: sortableHeader('Subject', 'subject'),
      cellClassName: 'application-testing-subject-cell',
      cell: (row) => (
        <span className="application-testing-subject" title={row.subject}>
          {row.subject}
        </span>
      ),
    },
    {
      id: 'tags',
      header: sortableHeader('Tags', 'tags'),
      headerClassName: 'application-testing-tags-cell',
      cell: (row) => <TagPicker ticketId={row.id} tags={ticketTags(row)} />,
    },
    { id: 'date', header: sortableHeader('Created on', 'date'), headerClassName: 'w-130', cell: (row) => row.date },
    {
      id: 'status',
      header: sortableHeader('Status', 'status'),
      headerClassName: 'application-testing-status-cell',
      cell: (row) => (
        <StatusPill value={row.statusId} disabled={isSaving} onChange={(status) => setStatus(row, status)} />
      ),
    },
    {
      id: 'priority',
      header: sortableHeader('Priority', 'priority'),
      headerClassName: 'application-testing-pill-cell',
      cell: (row) => (
        <PriorityPill value={row.priorityId} disabled={isSaving} onChange={(priority) => setPriority(row, priority)} />
      ),
    },
    {
      id: 'menu',
      header: '',
      headerClassName: 'application-testing-menu-cell',
      cell: (row) => <RowMenu onOpen={() => openDetails(row)} />,
    },
  ];

  return (
    <div className="form atd-page">
      <ApplicationTestingSidebar active="tickets" />
      <main className="ticket-wrapper support-ticket-page application-testing-list">
        <div className="application-testing-toolbar">
          <Button className="action-button primary-button application-testing-add" onClick={openRaiseDialog}>
            <Icon name="add" />
            <span>Application Testing</span>
          </Button>
          <label className="application-testing-search">
            <input
              type="text"
              placeholder="Search tickets, subject or description..."
              autoComplete="off"
              maxLength={100}
              value={searchText}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            <Icon name="search" />
          </label>
          <div className="application-testing-filters">
            <div className="application-testing-filter tag-filter">
              <Icon name="sell" outlined />
              <Select
                placeholder="All Tags"
                searchable={false}
                items={TAG_OPTIONS}
                bindLabel="name"
                bindValue="id"
                groupBy="group"
                value={tagFilter}
                onChange={(value) => onTagChange((value as string | null) ?? null)}
                openOnFocus
                panelClass="myPanelClass"
              />
            </div>
            <div className="application-testing-filter">
              <Select
                placeholder="Priority"
                searchable={false}
                items={priorityOptions}
                bindLabel="name"
                bindValue="id"
                value={priorityFilter}
                onChange={(value) => onPriorityChange(value as number | null)}
                openOnFocus
                panelClass="myPanelClass"
                renderLabel={renderPriorityLabel}
                renderOption={renderPriorityLabel}
              />
            </div>
            <div className="application-testing-filter">
              <Select
                placeholder="Status"
                searchable={false}
                items={statusOptions}
                bindLabel="name"
                bindValue="id"
                value={statusFilter}
                onChange={(value) => onStatusChange(value as number | null)}
                openOnFocus
                panelClass="myPanelClass application-testing-status-panel"
                panelMinWidth={STATUS_PANEL_MIN_WIDTH}
                renderLabel={renderStatusLabel}
                renderOption={renderStatusLabel}
              />
            </div>
            <button type="button" className="application-testing-clear" disabled={!hasFilters} onClick={clearFilters}>
              <Icon name="refresh" />
              Clear
            </button>
          </div>
        </div>

        {selectedRows.length > 0 && (
          <div className="application-testing-selection-bar">
            <span className="fw-bold">{selectedRows.length} selected</span>
            <Button className="action-button secondary-button" onClick={exportSelected}>
              <Icon name="border_all" />
              <span>Export to Excel</span>
            </Button>
            <button type="button" className="application-testing-clear" onClick={() => setSelection(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        <div className="application-testing-table-card">
          <div className="mat-table-wrapper no-hover">
            <DataTable
              stickyHeader
              columns={columns}
              rows={pageRows}
              rowKey={(row) => row.id}
              rowClassName={() => 'application-testing-clickable-row'}
              rowTitle={(row) => `View ${row.ticketNo}`}
              onRowClick={openDetails}
            />
          </div>
          {/* The app's standard pager, as on the Users and Clients lists. */}
          {sortedRows.length > 0 && (
            <Paginator
              length={sortedRows.length}
              pageSize={pageSize}
              pageIndex={currentPage - 1}
              pageSizeOptions={PAGE_SIZES}
              showFirstLastButtons
              onPage={(event) => {
                setPageSize(event.pageSize);
                setPage(event.pageIndex + 1);
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
